// src/components/Navbar/Navbar.tsx
'use client';

import React, { memo, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  FaBars,
  FaChevronDown,
  FaChevronLeft,
  FaHeadset,
  FaInfoCircle,
  FaNewspaper,
  FaRegBuilding,
  FaRegUser,
  FaSignOutAlt,
  FaTimes,
  FaUser
} from 'react-icons/fa';

import './Navbar.css';

import dynamic from 'next/dynamic';
import { apiClientWithoutAuth as apiClient } from '../../../utils/apiClient';

const AuthModal = dynamic(() => import('../LoginModal/AuthModal'), { ssr: false });

interface User {
  userId: string;
  username: string;
  phone: string;
  roles: string[];
}

type AuthActionContext =
  | 'desktop'
  | 'mobile-header'
  | 'mobile-sidebar';

interface ImportantCity {
  slug: string;
  name: string;
  caption: string;
  image: string;
  popular?: boolean;
}

// لیست شهرهای مهم ایران برای رزرو هتل
const IMPORTANT_CITIES: ImportantCity[] = [
  {
    slug: 'tehran',
    name: 'هتل‌های تهران',
    caption: 'پایتخت و مراکز اداری',
    image: '/images/city/tehran-tile.png',
    popular: true
  },
  {
    slug: 'mashhad',
    name: 'هتل‌های مشهد',
    caption: 'زیارتی و سیاحتی',
    image: '/images/city/mashad.webp',
    popular: true
  },
  {
    slug: 'kish',
    name: 'هتل‌های کیش',
    caption: 'مراکز تفریحی و خرید',
    image: '/images/city/kish.png',
    popular: true
  },
  {
    slug: 'isfahan',
    name: 'هتل‌های اصفهان',
    caption: 'آثار تاریخی و گردشگری',
    image: '/images/city/isfahan.jpg',
    popular: true
  },
  {
    slug: 'shiraz',
    name: 'هتل‌های شیراز',
    caption: 'شهر ادب، هنر و باغ‌ها',
    image: '/images/city/shiraz.jpg',
    popular: true
  },
  {
    slug: 'tabriz',
    name: 'هتل‌های تبریز',
    caption: 'شهر اولین‌ها و بازار کهن',
    image: '/images/city/tabriz.jpg'
  },
  {
    slug: 'yazd',
    name: 'هتل‌های یزد',
    caption: 'معماری اصیل و کویری',
    image: '/images/city/yazd.png'
  },
  {
    slug: 'qeshm',
    name: 'هتل‌های قشم',
    caption: 'طبیعت بکر و عجایب هفتگانه',
    image: '/images/city/qeshm.jpg'
  },
  {
    slug: 'rasht',
    name: 'هتل‌های رشت',
    caption: 'طبیعت سرسبز و خوراک‌شناسی',
    image: '/images/city/rasht.jpg'
  },
  {
    slug: 'ramsar',
    name: 'هتل‌های رامسر',
    caption: 'همنشینی کوهستان و دریا',
    image: '/images/city/ramsar.jpg'
  }
];

let BC_AUTH: BroadcastChannel | null = null;

try {
  if (
    typeof window !== 'undefined' &&
    typeof window.BroadcastChannel === 'function'
  ) {
    BC_AUTH = new BroadcastChannel('inhal_auth');
  }
} catch {
  BC_AUTH = null;
}

const Navbar: React.FC = memo(() => {
  const router = useRouter();

  const [menuOpen, setMenuOpen] = useState(false);
  const [hotelDropdownOpen, setHotelDropdownOpen] = useState(false);
  const [previewCitySlug, setPreviewCitySlug] = useState<string>(
    IMPORTANT_CITIES[0].slug
  );

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const hotelDropdownRef = useRef<HTMLLIElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  const normalizeRole = (value: string): string => {
    return value.trim().toLowerCase();
  };

  const isAdminUser = Boolean(
    user?.roles?.some((role) => {
      const normalizedRole = normalizeRole(role);

      return (
        normalizedRole === 'admin' ||
        normalizedRole === 'administrator' ||
        normalizedRole === 'superadmin'
      );
    })
  );

  const dashboardPath = isAdminUser
    ? '/dashboard'
    : '/user/dashboard';

  const columnOneCities = IMPORTANT_CITIES.slice(0, 5);
  const columnTwoCities = IMPORTANT_CITIES.slice(5);

  const previewCity =
    IMPORTANT_CITIES.find((city) => city.slug === previewCitySlug) ??
    IMPORTANT_CITIES[0];

  // =========================================================
  // بررسی وضعیت ورود کاربر
  // =========================================================
  useEffect(() => {
    let isMounted = true;

    const checkAuthStatus = async (): Promise<void> => {
      try {
        const response = await apiClient.get('/auth/me');

        if (!isMounted) {
          return;
        }

        const currentUser =
          response.data?.data ??
          response.data ??
          null;

        setUser(currentUser);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setUser(null);
      } finally {
        if (isMounted) {
          setAuthLoading(false);
        }
      }
    };

    void checkAuthStatus();

    return () => {
      isMounted = false;
    };
  }, []);

  // =========================================================
  // ورود به داشبورد
  // =========================================================
  const handleDashboardNavigation = (): void => {
    setUserMenuOpen(false);
    setMenuOpen(false);
    setHotelDropdownOpen(false);

    window.location.assign(dashboardPath);
  };

  // =========================================================
  // گوش دادن به تغییر وضعیت احراز هویت
  // =========================================================
  useEffect(() => {
    const handleAuthChange = (
      event: Event
    ): void => {
      const customEvent =
        event as CustomEvent<User | null>;

      setUser(customEvent.detail);
      setAuthLoading(false);
    };

    const handleBroadcastMessage = (
      event: MessageEvent
    ): void => {
      if (
        event.data &&
        event.data.type === 'authChanged'
      ) {
        setUser(event.data.user ?? null);
        setAuthLoading(false);
      }
    };

    window.addEventListener(
      'authChanged',
      handleAuthChange
    );

    BC_AUTH?.addEventListener(
      'message',
      handleBroadcastMessage
    );

    return () => {
      window.removeEventListener(
        'authChanged',
        handleAuthChange
      );

      BC_AUTH?.removeEventListener(
        'message',
        handleBroadcastMessage
      );
    };
  }, []);

  // =========================================================
  // خروج از حساب کاربری
  // =========================================================
  const handleLogout = async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error(
        'Failed to logout:',
        error
      );
    } finally {
      setUser(null);
      setUserMenuOpen(false);
      setMenuOpen(false);
      setHotelDropdownOpen(false);

      window.dispatchEvent(
        new CustomEvent<User | null>(
          'authChanged',
          {
            detail: null
          }
        )
      );

      BC_AUTH?.postMessage({
        type: 'authChanged',
        user: null
      });

      router.replace('/');
      router.refresh();
    }
  };

  // =========================================================
  // کنترل اسکرول صفحه هنگام باز بودن منو یا مودال
  // =========================================================
  useEffect(() => {
    const previousOverflow =
      document.body.style.overflow;

    if (menuOpen || isAuthModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [menuOpen, isAuthModalOpen]);

  // =========================================================
  // بستن منوها با کلیک بیرون
  // =========================================================
  useEffect(() => {
    const handleClickOutside = (
      event: MouseEvent
    ): void => {
      const target = event.target as Node;

      if (
        hotelDropdownRef.current &&
        !hotelDropdownRef.current.contains(target)
      ) {
        setHotelDropdownOpen(false);
      }

      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(target)
      ) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside
      );
    };
  }, []);

  // =========================================================
  // بازگشت پیش‌نمایش تصویر به شهر پیش‌فرض هنگام بسته شدن منو
  // =========================================================
  useEffect(() => {
    if (!hotelDropdownOpen) {
      setPreviewCitySlug(IMPORTANT_CITIES[0].slug);
    }
  }, [hotelDropdownOpen]);

  // =========================================================
  // بستن منوی موبایل پس از انتخاب لینک
  // =========================================================
  const closeMobileMenu = (): void => {
    setMenuOpen(false);
    setHotelDropdownOpen(false);
    setUserMenuOpen(false);
  };

  // رندر هر ردیف شهر در دسکتاپ
  const renderCityItem = (city: ImportantCity) => (
    <Link
      key={city.slug}
      href={`/hotel?city=${city.slug}`}
      className={`city-link-item ${
        city.slug === previewCitySlug ? 'is-previewing' : ''
      }`}
      onMouseEnter={() => setPreviewCitySlug(city.slug)}
      onFocus={() => setPreviewCitySlug(city.slug)}
      onClick={() => setHotelDropdownOpen(false)}
    >
      <div className="city-info">
        <span className="city-name">{city.name}</span>
        <span className="city-caption">{city.caption}</span>
      </div>
      {city.popular && <span className="city-popular-badge">پرطرفدار</span>}
    </Link>
  );

  // =========================================================
  // خروجی دکمه‌های احراز هویت
  // =========================================================
  const AuthActions: React.FC<{
    context: AuthActionContext;
  }> = ({ context }) => {
    if (authLoading) {
      return (
        <div
          className="auth-placeholder"
          aria-hidden="true"
        />
      );
    }

    if (user) {
      const displayName =
        user.username ||
        user.phone ||
        'حساب کاربری';

      const avatarInitial = displayName.charAt(0).toUpperCase();

      switch (context) {
        case 'desktop':
          return (
            <div
              className="user-dropdown-wrapper"
              ref={userMenuRef}
            >
              <button
                type="button"
                className="btn-login btn-account"
                aria-expanded={userMenuOpen}
                aria-haspopup="menu"
                onClick={() => {
                  setUserMenuOpen(
                    (previousState) =>
                      !previousState
                  );
                }}
              >
                <span className="user-avatar" aria-hidden="true">
                  {avatarInitial}
                </span>

                <span>{displayName}</span>

                <FaChevronDown
                  className="arrow-icon"
                  style={{
                    transform: userMenuOpen
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                    transition:
                      'transform 0.3s ease'
                  }}
                />
              </button>

              <div
                className={`user-dropdown-menu ${
                  userMenuOpen ? 'open' : ''
                }`}
                role="menu"
              >
                <div className="dropdown-user-info">
                  <span className="user-avatar user-avatar-lg" aria-hidden="true">
                    {avatarInitial}
                  </span>

                  <div className="dropdown-user-text">
                    <span className="dropdown-username">
                      {displayName}
                    </span>

                    {user.phone &&
                      user.username &&
                      user.username !== user.phone && (
                        <span className="dropdown-phone">
                          {user.phone}
                        </span>
                      )}
                  </div>
                </div>

                <hr className="dropdown-divider" />

                <button
                  type="button"
                  className="dropdown-item"
                  role="menuitem"
                  onClick={
                    handleDashboardNavigation
                  }
                >
                  <FaRegUser
                    style={{
                      marginLeft: '8px'
                    }}
                  />

                  {isAdminUser
                    ? 'پنل مدیریت'
                    : 'پنل کاربری'}
                </button>

                <hr className="dropdown-divider" />

                <button
                  type="button"
                  className="dropdown-item btn-logout"
                  role="menuitem"
                  onClick={() => {
                    void handleLogout();
                  }}
                >
                  <FaSignOutAlt
                    style={{
                      marginLeft: '8px'
                    }}
                  />

                  خروج از حساب
                </button>
              </div>
            </div>
          );

        case 'mobile-header':
          return (
            <button
              type="button"
              className="mobile-icon-btn mobile-avatar-btn"
              aria-label="باز کردن حساب کاربری"
              onClick={
                handleDashboardNavigation
              }
            >
              <span className="user-avatar" aria-hidden="true">
                {avatarInitial}
              </span>
            </button>
          );

        case 'mobile-sidebar':
          return (
            <div className="sidebar-user-info">
              <div className="user-details">
                <span className="user-avatar user-avatar-lg" aria-hidden="true">
                  {avatarInitial}
                </span>

                <span>{displayName}</span>
              </div>

              <button
                type="button"
                className="sidebar-login-btn"
                style={{
                  marginTop: '10px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textDecoration: 'none'
                }}
                onClick={
                  handleDashboardNavigation
                }
              >
                {isAdminUser
                  ? 'ورود به پنل مدیریت'
                  : 'ورود به پنل کاربری'}
              </button>

              <button
                type="button"
                className="sidebar-login-btn logout-mobile"
                style={{
                  marginTop: '10px'
                }}
                onClick={() => {
                  void handleLogout();
                }}
              >
                خروج از حساب
              </button>
            </div>
          );

        default:
          return null;
      }
    }

    switch (context) {
      case 'desktop':
        return (
          <button
            type="button"
            className="btn-login"
            onClick={() => {
              setIsAuthModalOpen(true);
            }}
          >
            <span>ورود / ثبت نام</span>

            <FaUser className="icon-user" />
          </button>
        );

      case 'mobile-header':
        return (
          <button
            type="button"
            className="mobile-icon-btn"
            aria-label="ورود یا ثبت نام"
            onClick={() => {
              setIsAuthModalOpen(true);
            }}
          >
            <FaRegUser className="mobile-icon-large" />
          </button>
        );

      case 'mobile-sidebar':
        return (
          <button
            type="button"
            className="sidebar-login-btn"
            onClick={() => {
              setMenuOpen(false);
              setIsAuthModalOpen(true);
            }}
          >
            ورود یا ثبت نام
          </button>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <nav
        className="iranija-navbar"
        dir="rtl"
      >
        <div className="iranija-navbar-container">
          {/* ================= DESKTOP LAYOUT ================= */}

          <div className="desktop-layout">
            <div className="nav-right">
              <Link
                href="/"
                className="nav-logo-link"
                onClick={() => {
                  setHotelDropdownOpen(false);
                  setUserMenuOpen(false);
                }}
              >
                <img
                  src="/icons/logo.png"
                  alt="لوگو"
                  className="main-logo"
                />
              </Link>

              <ul className="nav-list">
                <li
                  className="nav-item dropdown-parent"
                  ref={hotelDropdownRef}
                  onMouseEnter={() => {
                    setHotelDropdownOpen(true);
                  }}
                  onMouseLeave={() => {
                    setHotelDropdownOpen(false);
                  }}
                >
                  <button
                    type="button"
                    className="nav-link dropdown-toggle"
                    aria-expanded={
                      hotelDropdownOpen
                    }
                    onClick={() => {
                      setHotelDropdownOpen(
                        (previousState) =>
                          !previousState
                      );
                    }}
                  >
                    رزرو هتل

                    <FaChevronDown
                      className="arrow-icon"
                    />
                  </button>

                  <div
                    className={`mega-menu-overlay ${
                      hotelDropdownOpen
                        ? 'open'
                        : ''
                    }`}
                  >
                    <div className="mega-menu-container">
                      <div className="mega-menu-header">
                        <span className="mega-menu-title">
                          شهر مورد نظر را برای رزرو هتل انتخاب کنید
                        </span>
                      </div>

                      <div className="mega-cities-grid">
                        <div className="city-col">
                          {columnOneCities.map(renderCityItem)}
                        </div>
                        <div className="city-col">
                          {columnTwoCities.map(renderCityItem)}
                        </div>

                        <div className="mega-menu-promo">
                          <div className="mega-menu-preview-stage">
                            {IMPORTANT_CITIES.map((city) => (
                              <img
                                key={city.slug}
                                src={city.image}
                                alt={city.name}
                                draggable={false}
                                className={`mega-menu-preview-image ${
                                  city.slug === previewCitySlug
                                    ? 'is-active'
                                    : ''
                                }`}
                                onError={(event) => {
                                  const target = event.currentTarget;

                                  if (
                                    target.dataset.fallbackApplied !==
                                    'true'
                                  ) {
                                    target.dataset.fallbackApplied =
                                      'true';
                                    target.src =
                                      '/images/mega-menu-hotel.jpg';
                                  }
                                }}
                              />
                            ))}

                            <div
                              className="mega-menu-preview-gradient"
                              aria-hidden="true"
                            />

                            <div
                              key={previewCitySlug}
                              className="mega-menu-preview-caption"
                            >
                              <span className="mega-menu-preview-city-name">
                                {previewCity.name}
                              </span>

                              <span className="mega-menu-preview-city-caption">
                                {previewCity.caption}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mega-menu-footer">
                        <Link
                          href="/hotel"
                          className="mega-menu-all-link"
                          onClick={() => setHotelDropdownOpen(false)}
                        >
                          <span>مشاهده همه شهرها</span>
                          <FaChevronLeft className="mega-menu-all-arrow" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </li>

                <li className="nav-item">
                  <Link
                    href="/about"
                    className="nav-link"
                    onClick={() => {
                      setHotelDropdownOpen(false);
                    }}
                  >
                    درباره ما
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    href="/contact"
                    className="nav-link"
                    onClick={() => {
                      setHotelDropdownOpen(false);
                    }}
                  >
                    تماس با ما
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    href="/mag"
                    className="nav-link"
                    onClick={() => {
                      setHotelDropdownOpen(false);
                    }}
                  >
                    بلاگ
                  </Link>
                </li>
              </ul>
            </div>

            <div className="nav-left-actions">
              <button
                type="button"
                className="btn-support"
                aria-label="پشتیبانی آنلاین"
              >
                <span className="btn-support-label">پشتیبانی آنلاین</span>

                <span className="btn-support-icon-wrap" aria-hidden="true">
                  <FaHeadset className="icon-headset" />
                </span>
              </button>

              <AuthActions context="desktop" />
            </div>
          </div>

          {/* ================= MOBILE LAYOUT ================= */}

          <div className="mobile-layout">
            <div className="mobile-controls-left">
              <button
                type="button"
                className="mobile-menu-btn"
                aria-label="باز کردن منو"
                onClick={() => {
                  setMenuOpen(true);
                }}
              >
                <FaBars />
              </button>

              <FaChevronDown className="mobile-icon-small" />

              <AuthActions context="mobile-header" />

              <button
                type="button"
                className="mobile-icon-btn"
                aria-label="پشتیبانی آنلاین"
              >
                <FaHeadset className="mobile-icon-large" />
              </button>
            </div>

            <Link
              href="/"
              className="mobile-logo-link"
              onClick={closeMobileMenu}
            >
              <img
                src="/icons/logo.png"
                alt="لوگو ایرانجا"
                className="mobile-logo-img"
              />
            </Link>
          </div>
        </div>

        {/* پس‌زمینه منوی موبایل */}

        <div
          className={`sidebar-overlay ${
            menuOpen ? 'active' : ''
          }`}
          onClick={() => {
            setMenuOpen(false);
          }}
        />

        {/* منوی موبایل */}

        <div
          className={`mobile-sidebar ${
            menuOpen ? 'open' : ''
          }`}
        >
          <div className="sidebar-header">
            <button
              type="button"
              className="close-btn"
              aria-label="بستن منو"
              onClick={() => {
                setMenuOpen(false);
              }}
            >
              <FaTimes />
            </button>

            <span className="sidebar-title">
              منوی کاربری
            </span>
          </div>

          <ul className="sidebar-menu">
            <li className="sidebar-item-expandable">
              <button
                type="button"
                className="sidebar-link sidebar-disclosure"
                aria-expanded={hotelDropdownOpen}
                onClick={() => {
                  setHotelDropdownOpen((prev) => !prev);
                }}
              >
                <FaRegBuilding className="sidebar-icon" />

                رزرو هتل

                <FaChevronDown
                  className={`sidebar-arrow disclosure-arrow ${
                    hotelDropdownOpen ? 'open' : ''
                  }`}
                />
              </button>

              <div
                className={`sidebar-cities-panel ${
                  hotelDropdownOpen ? 'open' : ''
                }`}
              >
                <span className="sidebar-cities-intro">
                  شهر مورد نظر را انتخاب کنید
                </span>

                <div className="sidebar-cities-list">
                  {IMPORTANT_CITIES.map((city) => (
                    <Link
                      key={city.slug}
                      href={`/hotel?city=${city.slug}`}
                      className="sidebar-city-item"
                      onClick={closeMobileMenu}
                    >
                      <span>{city.name}</span>
                      <FaChevronLeft style={{ fontSize: '9px', color: '#999' }} />
                    </Link>
                  ))}
                </div>

                <Link
                  href="/hotel"
                  className="sidebar-cities-footer"
                  onClick={closeMobileMenu}
                >
                  مشاهده همه شهرها
                </Link>
              </div>
            </li>

            <li>
              <Link
                href="/about"
                className="sidebar-link"
                onClick={closeMobileMenu}
              >
                <FaInfoCircle className="sidebar-icon" />

                درباره ما

                <FaChevronLeft className="sidebar-arrow" />
              </Link>
            </li>

            <li>
              <Link
                href="/contact"
                className="sidebar-link"
                onClick={closeMobileMenu}
              >
                <FaHeadset className="sidebar-icon" />

                تماس با ما

                <FaChevronLeft className="sidebar-arrow" />
              </Link>
            </li>

            <li>
              <Link
                href="/mag"
                className="sidebar-link"
                onClick={closeMobileMenu}
              >
                <FaNewspaper className="sidebar-icon" />

                بلاگ

                <FaChevronLeft className="sidebar-arrow" />
              </Link>
            </li>
          </ul>

          <div className="sidebar-footer">
            <AuthActions context="mobile-sidebar" />
          </div>
        </div>
      </nav>

      {isAuthModalOpen ? (
        <AuthModal
          open
          onClose={() => {
            setIsAuthModalOpen(false);
          }}
        />
      ) : null}
    </>
  );
});

Navbar.displayName = 'Navbar';

export default Navbar;